import { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import { Accumulator, AccumulatorRequest } from "../../models/Accumulator";
import { LastServiceDate, LastServiceDateRequest } from "../../models/LastServiceDate";
import { IAccumulatorContext, AccumulatorContext } from "./AccumulatorContext";
import { useMembers } from "../members";
import { useUsers } from "../users";
import { UserTypes } from "../../models/UserTypes";
import { PlanAllotment } from "../../models/PlanAllotment";

export const AccumulatorProvider = (props: PropsWithChildren<any>) => {
    const { getWithQuery, postUnique } = useFetch();

    const { member, myInsuranceInfo } = useMembers();

    const {
        userType,
        userState: { loggedInUser },
    } = useUsers();

    const [accumulators, setAccumulators] = useState<Accumulator[]>([]);

    const [lastServiceDates, setLastServiceDates] = useState<LastServiceDate[]>([]);

    const [loading, setLoading] = useState(false);

    const [planAllotments, setPlanAllotments] = useState<PlanAllotment[]>([]);

    const getAccumulators = useCallback(
        async (groupNumber: string, participantIDNumber: string) => {
            const accumulators = await postUnique<AccumulatorRequest, Accumulator[]>("accumulators", {
                groupNumber,
                participantIDNumber,
            });
            return accumulators ? accumulators : [];
        },
        [postUnique]
    );

    const getLastServiceDates = useCallback(
        async (groupNumber: string, participantIDNumber: string) => {
            const serviceDates = await postUnique<LastServiceDateRequest, LastServiceDate[]>("lastservicedates", {
                groupNumber,
                participantIDNumber,
            });
            return serviceDates ? serviceDates : [];
        },
        [postUnique]
    );

    const getPlanAllotments = useCallback(
        async (groupNumber: string = "", planCodes: string[] = []) => {
            let results: PlanAllotment[] = [];
            for (const planCode of planCodes) {
                const allotments = await getWithQuery<PlanAllotment[]>("planallotments", { groupNumber, planCode });
                if (allotments) {
                    results = [...results, ...allotments];
                }
            }
            return results;
        },
        [getWithQuery]
    );

    const loadData = async (groupNumber: string, participantIdNumber: string, planCodes: string[]) => {
        try {
            setLoading(true);

            const [accumulators, lastServiceDates, planAllotments] = await Promise.all([
                getAccumulators(groupNumber, participantIdNumber),
                getLastServiceDates(groupNumber, participantIdNumber),
                getPlanAllotments(groupNumber, planCodes),
            ]);

            setAccumulators(accumulators);
            setLastServiceDates(lastServiceDates);
            setPlanAllotments(planAllotments);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (
            loggedInUser &&
            loggedInUser.groupNumber &&
            loggedInUser.participantIdNumber &&
            userType === UserTypes.Member &&
            myInsuranceInfo
        ) {
            loadData(
                loggedInUser.groupNumber,
                loggedInUser.participantIdNumber,
                myInsuranceInfo.coverageInformation.map((coverage) => coverage.productPlan)
            );
        }
    }, [loggedInUser, userType, myInsuranceInfo]);

    useEffect(() => {
        if (member) {
            loadData(member.groupNumber, member.participantIDNumber, [member.planCode]);
        }
    }, [member]);

    const value = useMemo(
        (): IAccumulatorContext => ({
            accumulators,
            lastServiceDates,
            loading,
            planAllotments,
        }),
        [accumulators, lastServiceDates, loading, planAllotments]
    );

    return <AccumulatorContext.Provider value={value} {...props} />;
};

export const useAccumulators = () => {
    const context = useContext(AccumulatorContext);
    if (context === undefined) {
        throw new Error("useAccumulators must be used within a AccumulatorProvider");
    }
    return context;
};
